import axios from './axios-configure'
import { AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js'
import jwt_decode from 'jwt-decode'
import React, { createContext, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { UserRole } from 'utils/Constants'
import UserPool from 'utils/UserPool'

const INACTIVITY_TIME_KEY = 'inactivityTime'
const MIN_BOUNDARY = 240

const AuthSessionContext = createContext(null)

const AuthSession = (props) => {
    const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false)
    const [user, setUser] = useState(undefined)
    const [userName, setUserName] = useState("")
    const [userRole, setUserRole] = useState(0)
    const navigate = useNavigate()

    const IS_ADMIN = userRole === UserRole.ADMIN

    const IS_OWNER = userRole === UserRole.OWNER

    const IS_USER = userRole === UserRole.USER

    const getOrganization = async () => {
        return await new Promise((resolve, reject) => {
            axios
                .get('/GetUserDefaultOrganization')
                .then(response => {
                    resolve(response.data)
                })
                .catch(err => {
                    reject(err)
                })
        })
    }

    const getOrganizationInfo = async () => {
        return await new Promise((resolve, reject) => {
            axios
                .get('/GetOrganizationInfo')
                .then(response => {
                    resolve(response.data)
                })
                .catch(err => {
                    reject(err)
                })
        })
    }

    const cleanOrganizationStorage = () => {
        localStorage.removeItem('OrganizationId')
        localStorage.removeItem('OrganizationName')
    }

    const getSession = async () => {
        return await new Promise(async () => {
            const user = UserPool.getCurrentUser()
            const inactivityTime = localStorage.getItem(INACTIVITY_TIME_KEY)
            const token = localStorage.getItem('jwtToken')

            if (!token && !inactivityTime) {
                navigate({ pathname: '/signin' }, { replace: true })
                return Promise.resolve('Request cancelled due to inactivity.');
            }      

            if (!inactivityTime)
                localStorage.setItem(INACTIVITY_TIME_KEY, new Date());
            else{
                const isInactivityInvalid = Math.round((new Date() - new Date(inactivityTime)) / 60000) >= MIN_BOUNDARY

                if (isInactivityInvalid) {
                    localStorage.removeItem(INACTIVITY_TIME_KEY)
                    localStorage.setItem('jwtToken', "")
                    setUserName("")
                    cleanOrganizationStorage()
                    toast.dismiss()
                    localStorage.setItem('isTimeout', true)
                    navigate({ pathname: '/signin' }, { replace: true })
                    if(user)
                        user.signOut()
                } else {
                    localStorage.setItem(INACTIVITY_TIME_KEY, new Date());
                }
            }

            if (user) {
                const organizationId = localStorage.getItem('OrganizationId')
                if (!organizationId) {
                    getOrganization()
                        .then(result => {
                            localStorage.setItem('OrganizationId', result.id)
                            localStorage.setItem('OrganizationName', result.orgName)
                            window.dispatchEvent(new Event('OrganizationUpdated'))
                            setUserRole(result.role)
                        })
                        .catch(err => {
                            console.log(err)
                        })
                } else {
                    getOrganizationInfo()
                        .then(result => {
                            localStorage.setItem('OrganizationId', result.id)
                            localStorage.setItem('OrganizationName', result.orgName)
                            window.dispatchEvent(new Event('OrganizationUpdated'))
                            setUserRole(result.role)
                        })
                        .catch(err => {
                            console.log(err)
                        })
                }

                user.getSession((err, session) => {
                    if (err || !session.isValid()) {
                        localStorage.removeItem(INACTIVITY_TIME_KEY)
                        localStorage.setItem('jwtToken', "")
                        setUserName("")
                        cleanOrganizationStorage()
                        toast.dismiss()
                        localStorage.setItem('isTimeout', true)
                        navigate({ pathname: '/signin' }, { replace: true })
                        user.signOut()
                        
                    } else {
                        const decodedSession = jwt_decode(
                            session.idToken.jwtToken
                        )
                        setUserName(decodedSession.email)
                    }
                })
            } else {
                localStorage.removeItem(INACTIVITY_TIME_KEY)
                localStorage.setItem('jwtToken', "")
                setUserName("")
                cleanOrganizationStorage()
                toast.dismiss()
                localStorage.setItem('isTimeout', true)
                navigate({ pathname: '/signin' }, { replace: true })
            }
        })
    }

    const authenticate = async (Username, Password) => {
        cleanOrganizationStorage()
        return await new Promise((resolve, reject) => {
            const cognitoUser = new CognitoUser({ Username, Pool: UserPool })
            const authDetails = new AuthenticationDetails({
                Username,
                Password
            })

            cognitoUser.authenticateUser(authDetails, {
                onSuccess: (data) => {
                    resolve(data)
                    localStorage.setItem('isAuthenticated', true)
                    localStorage.setItem('jwtToken', data.accessToken.jwtToken)
                    getOrganization()
                        .then(result => {
                            axios.post('/UpdateOrgUserInfo')

                            localStorage.setItem('OrganizationId', result.id)
                            localStorage.setItem('OrganizationName', result.orgName)
                            window.dispatchEvent(new Event('OrganizationUpdated'))
                            setUserRole(result.role)
                            axios
                                .get('/CheckUserHasInfo')
                                .then(response => {
                                    if (!response.data) {
                                        navigate('/setup-account', { replace: true })
                                    } else {
                                        navigate('/dashboard', { replace: true })
                                    }
                                })
                                .catch(err => {
                                    console.error(err)
                                })
                        })
                        .catch(err => {
                            console.error(err)
                        })
                },
                onFailure: (err) => {
                    reject(err)
                },
                newPasswordRequired: (data) => {
                    setIsNewPasswordRequired(true)
                    setUser(cognitoUser)
                },
            })
        })
    }

    const changeTemporalPassword = async (newPassword) => {
        return await new Promise((resolve, reject) => {
            user.completeNewPasswordChallenge(newPassword, {}, {
                onSuccess: (data) => {
                    resolve(data)
                    setIsNewPasswordRequired(false)
                    navigate('/signin', { replace: true })
                },
                onFailure: (err) => {
                    reject(err)
                },
            })
        })
    }

    const logout = () => {
        const user = UserPool.getCurrentUser()
        if (user) {
            user.signOut()
            setUserName("")
            localStorage.setItem('isTimeout', false)
            localStorage.setItem('isAuthenticated', false)
            localStorage.removeItem('jwtToken')
            localStorage.removeItem('inactivityTime')
            cleanOrganizationStorage()
            navigate('/signin', { replace: true })
        }
    }

    const sendCode = async (email) => {
        return await new Promise((resolve, reject) => {
            const user = new CognitoUser({ Username: email, Pool: UserPool })

            user.forgotPassword({
                onSuccess: (data) => {
                    resolve(data)
                },
                onFailure: (err) => {
                    reject(err)
                },
                inputVerificationCode: (data) => {
                    resolve(data)
                },
            })
        })
    }

    const resetPassword = async (code, password, email) => {
        return await new Promise((resolve, reject) => {
            const user = new CognitoUser({ Username: email, Pool: UserPool })

            user.confirmPassword(code, password, {
                onSuccess: (data) => {
                    resolve(data)
                    navigate('/signin', { replace: true })
                },
                onFailure: (err) => {
                    reject(err)
                },
            })
        })
    }

    return (
        <AuthSessionContext.Provider
            value={{
                authenticate,
                getSession,
                logout,
                sendCode,
                resetPassword,
                isNewPasswordRequired,
                changeTemporalPassword,
                userName,
                setUserName,
                setUserRole,
                IS_ADMIN,
                IS_OWNER,
                IS_USER
            }}
        >
            {props.children}
        </AuthSessionContext.Provider>
    )
}

export { AuthSession, AuthSessionContext }

